import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { api, ApiError } from '@/lib/api';
import type { UserRole } from '@msk/shared';
import { toast } from '@/components/ui/toast';
import i18n from '@/i18n';
import { runningBundle } from '@/lib/versionNudge';
import { reloadIfStaleAtLogin } from '@/lib/staleBundleAtLogin';
import { sweepClinicalStorage } from '@/lib/clinicalStorageSweep';

// ── Types ──────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string;
  title?: string;
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  clinicId: string;
  clinicName?: string;
  /**
   * KPI K1-2 — the clinic-scoped owner flag from GET /api/auth/me
   * (`users.is_clinic_owner`). It decides whether the KPI Portal item is
   * rendered; it decides nothing on the server, which re-reads the flag on
   * every /api/kpi request and answers 404 without it.
   */
  isClinicOwner?: boolean;
  /** KPI R3 — this user may see the staff board: the owner, or flagged by the owner (UI hint only). */
  staffBoardVisible?: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<LoginResponse>;
  verify2FA: (tempToken: string, code: string) => Promise<LoginResponse>;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
  sessionWarning: boolean;
  dismissSessionWarning: () => void;
  forcePasswordChange: boolean;
}

interface LoginResponse {
  /** Run N-5 — the page must not navigate: a reload is in flight. */
  reloading?: boolean;
  requires2FA?: boolean;
  tempToken?: string;
  forcePasswordChange?: boolean;
}

const LOGIN_RELOAD_MARKER = 'sc_login_reloaded_for';
/** Run N-5 — real deps for reloadIfStaleAtLogin; the lib stays DOM-free. */
function staleLoginDeps() {
  return {
    running: runningBundle(),
    fetchServed: async (): Promise<string | null | undefined> => {
      const res = await api.get<{ bundle?: string | null }>('/version');
      return res.success ? res.data?.bundle : null;
    },
    reload: (): void => { window.location.reload(); },
    marker: {
      get: (): string | null => { try { return sessionStorage.getItem(LOGIN_RELOAD_MARKER); } catch { return null; } },
      set: (v: string): void => { try { sessionStorage.setItem(LOGIN_RELOAD_MARKER, v); } catch { /* private mode */ } },
    },
  };
}

// ── Context ────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_KEY = 'msk_token';
const USER_KEY = 'msk_user';
const LAST_CHECK_KEY = 'msk_getme_last_check';
const LAST_ACTIVITY_KEY = 'msk_last_activity';
// Cache getMe result for 60 seconds so nav bursts don't re-hit /auth/me 20x
const GET_ME_CACHE_MS = 60_000;
// Auto-logout after 8 hours of inactivity. Previously 30 min which
// silently logged out clinicians mid-session (see proof/post-v2/session-ux/).
// A full clinical day is ~8h. GDPR 2H still satisfied via the explicit
// logout button + absolute JWT expiry on the backend.
const INACTIVITY_TIMEOUT_MS = 8 * 60 * 60 * 1000;
// Show warning 5 minutes before timeout (i.e. at 7h 55min of inactivity).
const INACTIVITY_WARNING_MS = INACTIVITY_TIMEOUT_MS - 5 * 60 * 1000;

// ── Provider ───────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      const stored = localStorage.getItem(USER_KEY);
      return stored ? (JSON.parse(stored) as AuthUser) : null;
    } catch {
      return null;
    }
  });

  const [isLoading, setIsLoading] = useState<boolean>(() => {
    return !!localStorage.getItem(TOKEN_KEY);
  });

  const [sessionWarning, setSessionWarning] = useState(false);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);

  // Validate stored token on mount (once per 60s)
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setIsLoading(false);
      return;
    }

    const lastCheckRaw = localStorage.getItem(LAST_CHECK_KEY);
    const lastCheck = lastCheckRaw ? Number(lastCheckRaw) : 0;
    if (Date.now() - lastCheck < GET_ME_CACHE_MS && user) {
      setIsLoading(false);
      return;
    }

    api.get<AuthUser>('/auth/me')
      .then((res) => {
        if (res.success && res.data) {
          setUser(res.data);
          localStorage.setItem(USER_KEY, JSON.stringify(res.data));
          localStorage.setItem(LAST_CHECK_KEY, String(Date.now()));
        }
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          localStorage.removeItem(LAST_CHECK_KEY);
          sweepClinicalStorage(); // Sprint B / B-6
          setUser(null);
        }
      })
      .finally(() => setIsLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email: string, password: string): Promise<LoginResponse> => {
    const res = await api.post<{
      token?: string;
      user?: AuthUser;
      requires2FA?: boolean;
      tempToken?: string;
      forcePasswordChange?: boolean;
    }>('/auth/login', { email, password });

    if (!res.success || !res.data) {
      throw new Error(res.error ?? 'Login failed');
    }

    // 2FA required — don't set user yet
    if (res.data.requires2FA) {
      return { requires2FA: true, tempToken: res.data.tempToken };
    }

    // Normal login
    if (res.data.token && res.data.user) {
      localStorage.setItem(TOKEN_KEY, res.data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(res.data.user));
      localStorage.setItem(LAST_CHECK_KEY, String(Date.now()));
      // Run N-5 — a stale cached bundle reloads HERE, before the dashboard
      // renders; the session is already stored so the reload lands signed in.
      if (await reloadIfStaleAtLogin(staleLoginDeps()) === 'reloaded') return { reloading: true };
      setUser(res.data.user);
      if (res.data.forcePasswordChange) {
        setForcePasswordChange(true);
        return { forcePasswordChange: true };
      }
    }

    return {};
  }, []);

  const verify2FA = useCallback(async (tempToken: string, code: string): Promise<LoginResponse> => {
    const res = await api.post<{
      token: string;
      user: AuthUser;
      forcePasswordChange?: boolean;
    }>('/auth/2fa/verify', { tempToken, code });

    if (!res.success || !res.data) {
      throw new Error(res.error ?? '2FA verification failed');
    }

    localStorage.setItem(TOKEN_KEY, res.data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(res.data.user));
    localStorage.setItem(LAST_CHECK_KEY, String(Date.now()));
    if (await reloadIfStaleAtLogin(staleLoginDeps()) === 'reloaded') return { reloading: true };
    setUser(res.data.user);

    if (res.data.forcePasswordChange) {
      setForcePasswordChange(true);
    }
    return {};
  }, []);

  const logout = useCallback((): void => {
    // fix(auth-safe-fetches) BR-11: a failed revocation used to be swallowed,
    // leaving the refresh token alive server-side while the UI showed logged
    // out. Retry once; still failing is WARNED, and the client still clears
    // its local session either way.
    api.post('/auth/logout', {}).catch(() =>
      api.post('/auth/logout', {}).catch(() =>
        console.warn('[auth] logout revocation failed after retry; local session cleared, server-side refresh token may persist until expiry')
      )
    );
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(LAST_CHECK_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    // Sprint B / B-6 (deep-audit F-05): remove every clinical key a browser
    // may still hold from before the writes were removed (autosave:*,
    // msk_complaints_*) plus the per-assessment step cursor.
    sweepClinicalStorage();
    setUser(null);
    setSessionWarning(false);
    setForcePasswordChange(false);
  }, []);

  const dismissSessionWarning = useCallback(() => {
    setSessionWarning(false);
    // Reset activity timer
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
  }, []);

  // ── Inactivity auto-logout with warning ─────────────────────────────────
  useEffect(() => {
    if (!user) return;

    const touch = () => {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
      setSessionWarning(false);
    };
    touch();

    const events: (keyof WindowEventMap)[] = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((e) => window.addEventListener(e, touch, { passive: true }));

    const interval = setInterval(() => {
      const last = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || Date.now());
      const elapsed = Date.now() - last;

      if (elapsed > INACTIVITY_TIMEOUT_MS) {
        console.warn('[Auth] Session timed out after 8h inactivity');
        toast.error(
          i18n.t('session.expired.toast', 'Your session expired. Please log in again.'),
          5000,
        );
        logout();
      } else if (elapsed > INACTIVITY_WARNING_MS) {
        setSessionWarning(true);
      }
    }, 15_000); // check every 15 seconds for warning

    return () => {
      events.forEach((e) => window.removeEventListener(e, touch));
      clearInterval(interval);
    };
  }, [user, logout]);

  return (
    <AuthContext.Provider
      value={{
        user, login, verify2FA, logout, isLoading,
        isAuthenticated: !!user,
        sessionWarning, dismissSessionWarning,
        forcePasswordChange,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
